import React, { useState, useEffect, useContext } from 'react';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentPlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon as ChevronRightIcon2,
  CheckIcon,
  CloudArrowUpIcon,
  EyeIcon,
  ArrowUpTrayIcon,
  BeakerIcon,
  ClockIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import { AddDogModal, EditDogModal } from '../components/DogManagementModals';
import StatPill from '../components/v2/StatPill';

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string;
  rasse?: string;
  geburtstag?: string;
  chipnummer?: string;
  tassonummer?: string;
  tollwut_end_date?: string;
  geschlecht?: string;
  kastriert?: boolean;
  hundebox?: string;
  insurance_name?: string;
  valid_tollwut: boolean;
  valid_insurance: boolean;
  tollwut_checked_at?: string;
  tollwut_checked_by?: string;
  insurance_checked_at?: string;
  insurance_checked_by?: string;
  image_path_updated?: string;
  documents: {
    insurance: Document[];
    tollwut: Document[];
  };
}

interface Document {
  docId: number;
  filename: string;
  created_at: string;
}

interface Member {
  member_nr: string;
  member_name: string;
  foto_path_updated?: string;
  dogs: Dog[];
}

const DogManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant: 'single' | 'two';
    onConfirm?: () => void;
    buttons?: {
      cancel: { text: string; onPress: () => void };
      confirm: { text: string; onPress: () => void };
    };
    button?: { text: string; onPress: () => void };
  }>({ visible: false, type: 'info', title: '', message: '', variant: 'single' });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ok' | 'pending' | 'issues'>('all');
  const [hideNoDogs, setHideNoDogs] = useState(true);
  const [showExpiredTollwut, setShowExpiredTollwut] = useState(false);
  
  // UI states
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [selectedDog, setSelectedDog] = useState<Dog | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  
  // Modal states
  const [showAddDogModal, setShowAddDogModal] = useState(false);
  const [showEditDogModal, setShowEditDogModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [documentType, setDocumentType] = useState<'insurance' | 'tollwut'>('insurance');
  const [selectedDocType, setSelectedDocType] = useState<'insurance' | 'tollwut'>('insurance');
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);

  useEffect(() => {
    fetchAllDogsStatus();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [members, searchTerm, statusFilter, hideNoDogs, showExpiredTollwut]);

  const fetchAllDogsStatus = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/members/all_dogs_status');
      if (response.data.success) {
        setMembers(response.data.data.members);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehler beim Laden der Daten.',
          variant: 'single'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...members];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.member_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(member => {
        const status = getMemberStatus(member);
        if (statusFilter === 'ok') return status.variant === 'success';
        if (statusFilter === 'pending') return status.variant === 'warning';
        if (statusFilter === 'issues') return status.variant === 'danger';
        return true;
      });
    }

    // Hide members without dogs
    if (hideNoDogs) {
      filtered = filtered.filter(member => member.dogs.length > 0);
    }

    // Show only expired Tollwut
    if (showExpiredTollwut) {
      filtered = filtered.filter(member =>
        member.dogs.some(dog =>
          dog.valid_tollwut &&
          dog.tollwut_end_date &&
          new Date(dog.tollwut_end_date) < new Date()
        )
      );
    }

    // Sort by status priority
    filtered.sort((a, b) => {
      const statusA = getMemberStatus(a);
      const statusB = getMemberStatus(b);
      const priorityA = statusA.variant === 'danger' ? 3 : statusA.variant === 'warning' ? 2 : 1;
      const priorityB = statusB.variant === 'danger' ? 3 : statusB.variant === 'warning' ? 2 : 1;
      
      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }
      
      return a.member_name.localeCompare(b.member_name);
    });

    setFilteredMembers(filtered);
  };

  const getMemberStatus = (member: Member) => {
    const dogCount = member.dogs.length;
    const validCount = member.dogs.filter(dog => dog.valid_insurance && dog.valid_tollwut).length;
    const pendingCount = member.dogs.reduce((total, dog) => {
      const pendingInsurance = dog.documents?.insurance?.length > 0 && !dog.valid_insurance ? 1 : 0;
      const pendingTollwut = dog.documents?.tollwut?.length > 0 && !dog.valid_tollwut ? 1 : 0;
      return total + pendingInsurance + pendingTollwut;
    }, 0);
    const missingDocs = member.dogs.some(
      dog => !dog.documents?.insurance?.length || !dog.documents?.tollwut?.length
    );

    if (dogCount === 0) return { text: 'Keine Hunde', variant: 'secondary', count: `${validCount}/${dogCount}` };
    if (!missingDocs && pendingCount === 0) return { text: 'Alles OK', variant: 'success', count: `${validCount}/${dogCount}` };
    if (pendingCount > 0) return { text: 'Zu prüfen', variant: 'warning', count: `${validCount}/${dogCount}` };
    if (missingDocs) return { text: 'Dokumente fehlen', variant: 'danger', count: `${validCount}/${dogCount}` };
    return { text: 'Unbekannt', variant: 'secondary', count: `${validCount}/${dogCount}` };
  };

  const getStats = () => {
    const totalMembers = members.length;
    const totalDogs = members.reduce((sum, member) => sum + member.dogs.length, 0);
    const validInsurances = members.reduce(
      (sum, member) => sum + member.dogs.filter(dog => dog.valid_insurance).length,
      0
    );
    const validVaccinations = members.reduce(
      (sum, member) => sum + member.dogs.filter(dog => dog.valid_tollwut).length,
      0
    );
    const membersWithIssues = members.filter(member => getMemberStatus(member).variant === 'danger').length;
    const membersPending = members.filter(member => getMemberStatus(member).variant === 'warning').length;
    
    return { totalMembers, totalDogs, validInsurances, validVaccinations, membersWithIssues, membersPending };
  };

  const stats = getStats();

  const toggleMemberExpansion = (memberId: string) => {
    setExpandedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const handleAddDog = (memberId: string) => {
    setSelectedMemberId(memberId);
    setShowAddDogModal(true);
  };

  const handleEditDog = (dog: Dog, memberId: string) => {
    setSelectedDog(dog);
    setSelectedMemberId(memberId);
    setShowEditDogModal(true);
  };

  const handleDeleteDog = (dogId: number) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Hund löschen',
      message: 'Möchten Sie diesen Hund wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      variant: 'two',
      buttons: {
        cancel: { 
          text: 'Abbrechen', 
          onPress: () => setConfirmModal(prev => ({...prev, visible: false})) 
        },
        confirm: { 
          text: 'Löschen', 
          onPress: () => { 
            handleDeleteConfirm(dogId); 
            setConfirmModal(prev => ({...prev, visible: false})); 
          }
        }
      }
    });
  };

  const handleDeleteConfirm = async (dogId: number) => {
    try {
      const response = await apiClient.delete(`/api/dogs/${dogId}`);
      if (response.data.success) {
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Hund erfolgreich gelöscht!',
          variant: 'single',
          button: { 
            text: 'OK', 
            onPress: () => setConfirmModal(prev => ({...prev, visible: false}))
          }
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Löschen des Hundes.',
        variant: 'single',
        button: { 
          text: 'OK', 
          onPress: () => setConfirmModal(prev => ({...prev, visible: false}))
        }
      });
    }
  };

  const handleValidateInsurance = async (dogId: number) => {
    try {
      const response = await apiClient.post(`/api/dogs/validate_insurance/${dogId}`);
      if (response.data.success) {
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Versicherung bestätigt!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Bestätigen der Versicherung.',
        variant: 'single'
      });
    }
  };

  const handleValidateTollwut = async (dogId: number) => {
    try {
      const response = await apiClient.post(`/api/dogs/validate_tollwut/${dogId}`);
      if (response.data.success) {
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Impfung bestätigt!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Bestätigen der Impfung.',
        variant: 'single'
      });
    }
  };

  const handleRevokeInsurance = async (dogId: number) => {
    try {
      const response = await apiClient.post(`/api/dogs/unvalidate_insurance/${dogId}`);
      if (response.data.success) {
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Versicherungsstatus entzogen!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Entziehen des Versicherungsstatus.',
        variant: 'single'
      });
    }
  };

  const handleRevokeTollwut = async (dogId: number) => {
    try {
      const response = await apiClient.post(`/api/dogs/unvalidate_tollwut/${dogId}`);
      if (response.data.success) {
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Impfstatus entzogen!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Entziehen des Impfstatus.',
        variant: 'single'
      });
    }
  };

  const handleViewDocuments = (dog: Dog, type: 'insurance' | 'tollwut') => {
    setSelectedDog(dog);
    setDocumentType(type);
    setSelectedDocuments(type === 'insurance' ? dog.documents.insurance : dog.documents.tollwut);
    setCurrentDocIndex(0);
    setShowDocumentModal(true);
  };

  const handleAddDogSubmit = async (formData: FormData) => {
    formData.append('member_id', selectedMemberId || '');
    
    try {
      const response = await apiClient.post('/api/dogs/add_dog', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setShowAddDogModal(false);
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Hund erfolgreich hinzugefügt!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Hinzufügen des Hundes.',
        variant: 'single'
      });
    }
  };

  const handleEditDogSubmit = async (formData: FormData, dogId: number) => {
    // Find the member_nr for this dog
    let memberIdToUpdate = null;
    for (const member of members) {
      const foundDog = member.dogs.find((dog: Dog) => dog.dog_id === dogId);
      if (foundDog) {
        memberIdToUpdate = member.member_nr;
        break;
      }
    }

    if (!memberIdToUpdate) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Mitglied für diesen Hund nicht gefunden.',
        variant: 'single'
      });
      return;
    }

    // Add member_nr to formData if needed
    formData.append('member_nr', memberIdToUpdate.toString());

    try {
      const response = await apiClient.put(`/api/dogs/${dogId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setShowEditDogModal(false);
        await fetchAllDogsStatus();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Hund erfolgreich aktualisiert!',
          variant: 'single'
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Aktualisieren des Hundes.',
        variant: 'single'
      });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getDogStatusColor = (dog: Dog) => {
    if (dog.valid_insurance && dog.valid_tollwut) return 'text-success-600';
    if (dog.documents?.insurance?.length > 0 || dog.documents?.tollwut?.length > 0) return 'text-warning-600';
    return 'text-error-600';
  };

  const getDogStatusIcon = (dog: Dog) => {
    if (dog.valid_insurance && dog.valid_tollwut) {
      return <CheckCircleIcon className="h-5 w-5 text-success-600" />;
    }
    if (dog.documents?.insurance?.length > 0 || dog.documents?.tollwut?.length > 0) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-warning-600" />;
    }
    return <XCircleIcon className="h-5 w-5 text-error-600" />;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Skeleton Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        {/* Skeleton Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 lg:mb-6">
        {members.length > 0 && (
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Mitglied suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-10 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        )}
        <button
          onClick={fetchAllDogsStatus}
          className="h-11 px-3 flex items-center gap-2 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          title="Aktualisieren"
        >
          <ArrowPathIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Aktualisieren</span>
        </button>
      </div>

      {/* Stats (StatPill) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        <StatPill value={stats.validInsurances} label="Versichert" tone="success" />
        <StatPill value={stats.validVaccinations} label="Geimpft" tone="info" />
        <StatPill value={stats.membersPending} label="Zu prüfen" tone="warning" />
        <StatPill value={stats.membersWithIssues} label="Probleme" tone="danger" />
      </div>

      {/* Filters */}
      <div className="mb-4 sm:mb-6 bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                statusFilter === 'all' 
                  ? 'bg-info-500 text-white dark:bg-info-600 shadow-lg transform scale-105' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Alle
            </button>
            <button
              onClick={() => setStatusFilter('ok')}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                statusFilter === 'ok' 
                  ? 'bg-success-500 text-white dark:bg-success-600 shadow-lg transform scale-105' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Alles OK
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                statusFilter === 'pending' 
                  ? 'bg-warning-500 text-white dark:bg-warning-600 shadow-lg transform scale-105' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Zu prüfen
            </button>
            <button
              onClick={() => setStatusFilter('issues')}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                statusFilter === 'issues' 
                  ? 'bg-error-500 text-white dark:bg-error-600 shadow-lg transform scale-105' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Probleme
            </button>
          </div>

          {/* Additional Filter Options */}
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer group">
              <input
                type="checkbox"
                checked={hideNoDogs}
                onChange={(e) => setHideNoDogs(e.target.checked)}
                className="h-4 w-4 text-info-500 focus:ring-info-500 border-gray-300 dark:border-gray-600 rounded transition-all group-hover:scale-110"
              />
              <span className="group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                Nur mit Hunden
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer group">
              <input
                type="checkbox"
                checked={showExpiredTollwut}
                onChange={(e) => setShowExpiredTollwut(e.target.checked)}
                className="h-4 w-4 text-info-500 focus:ring-info-500 border-gray-300 dark:border-gray-600 rounded transition-all group-hover:scale-110"
              />
              <span className="group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                Abgelaufene Impfungen
              </span>
            </label>
          </div>
        </div>
      </div>


      {/* Members List */}
      <div className="space-y-4">
        {filteredMembers.map((member) => {
          const isExpanded = expandedMembers.has(member.member_nr);
          const status = getMemberStatus(member);
          
          return (
            <div
              key={member.member_nr}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Member Header - Mobile Optimized */}
              <div
                onClick={() => toggleMemberExpansion(member.member_nr)}
                className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button className="p-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    
                    {/* Member Profile Image - Display Only */}
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                      <img
                        src={`${BASE_URL}/api/members/user-image-url/${member.member_nr}?size=S&token=${localStorage.getItem('jwt_token')}&t=${member.foto_path_updated || ''}`}
                        alt={member.member_name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="h-full w-full flex items-center justify-center bg-primary-600">
                                <svg class="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                              </div>
                            `;
                          }
                        }}
                      />
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {member.member_name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        #{member.member_nr} · {member.dogs.length} {member.dogs.length === 1 ? 'Hund' : 'Hunde'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {status.count}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      status.variant === 'success' 
                        ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400'
                        : status.variant === 'warning'
                        ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400'
                        : status.variant === 'danger'
                        ? 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                    }`}>
                      {status.text}
                    </span>
                  </div>
                </div>

                {/* Mobile Layout - Clean and Clear */}
                <div className="sm:hidden">
                  <div className="flex items-start gap-3">
                    {/* Expand Icon */}
                    <div className="pt-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    {/* Profile Image */}
                    <div className="h-14 w-14 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                      <img
                        src={`${BASE_URL}/api/members/user-image-url/${member.member_nr}?size=S&token=${localStorage.getItem('jwt_token')}&t=${member.foto_path_updated || ''}`}
                        alt={member.member_name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="h-full w-full flex items-center justify-center bg-gray-500">
                                <svg class="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                              </div>
                            `;
                          }
                        }}
                      />
                    </div>
                    
                    {/* Member Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                            {member.member_name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Mitglied #{member.member_nr}
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">
                            {status.count} {member.dogs.length === 1 ? 'Hund' : 'Hunde'}
                          </p>
                        </div>
                        
                        {/* Status Badge */}
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                          status.variant === 'success'
                            ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                            : status.variant === 'warning'
                            ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300'
                            : status.variant === 'danger'
                            ? 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dogs List */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="p-3 sm:p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Hunde
                      </h4>
                      <button
                        onClick={() => handleAddDog(member.member_nr)}
                        className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <PlusCircleIcon className="h-4 w-4" />
                        Hund hinzufügen
                      </button>
                    </div>

                    {member.dogs.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        Keine Hunde registriert
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {member.dogs.map((dog) => {
                          const hasIssues = !dog.valid_insurance || !dog.valid_tollwut || 
                                         (!dog.documents?.insurance?.length && !dog.documents?.tollwut?.length);
                          const hasPending = (dog.documents?.insurance?.length > 0 && !dog.valid_insurance) ||
                                           (dog.documents?.tollwut?.length > 0 && !dog.valid_tollwut);
                          
                          return (
                          <div
                            key={dog.dog_id}
                            className={`rounded-xl shadow-sm border-2 transition-all duration-200 overflow-hidden ${
                              hasIssues ? 'border-error-200 dark:border-error-800 bg-error-50/30 dark:bg-error-900/10' :
                              hasPending ? 'border-warning-200 dark:border-warning-800 bg-warning-50/30 dark:bg-warning-900/10' :
                              'border-success-200 dark:border-success-800 bg-success-50/30 dark:bg-success-900/10'
                            } hover:shadow-md`}
                          >
                            {/* Mobile View with Image Overlay */}
                            <div className="sm:hidden">
                              <div className="relative h-48 w-full">
                                <img
                                  src={`${BASE_URL}/api/dogs/image-url/${dog.dog_id}?size=M&token=${localStorage.getItem('jwt_token')}&t=${dog.image_path_updated || ''}`}
                                  alt={dog.hundename}
                                  className="absolute inset-0 w-full h-full object-cover"
                                      onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const parent = e.currentTarget.parentElement;
                                    if (parent) {
                                      parent.innerHTML = `
                                        <div class="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-800">
                                          <svg class="h-16 w-16 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M4.5 10.5C4.5 7.5 7 5 10 5s5.5 2.5 5.5 5.5c0 2.28-1.2 4.3-3 5.5v4.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5V16c-1.8-1.2-3-3.22-3-5.5zm2 0c0 1.48.8 2.77 2 3.48v3.52c0 .28.22.5.5.5s.5-.22.5-.5v-3.52c1.2-.71 2-2 2-3.48 0-2.21-1.79-4-4-4s-4 1.79-4 4zm11.5 1c0 1.5-.5 2.9-1.4 4l.9.5c1-1.3 1.5-2.8 1.5-4.5 0-3.9-3.1-7-7-7-.7 0-1.4.1-2 .3l.5.9c.5-.1 1-.2 1.5-.2 3.3 0 6 2.7 6 6z"/>
                                          </svg>
                                        </div>
                                      `;
                                    }
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/50" />
                                <div className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md ${
                                  hasIssues ? 'bg-error-500' : hasPending ? 'bg-warning-500' : 'bg-success-500'
                                }`}>
                                  {hasIssues ? (
                                    <ExclamationTriangleIcon className="w-4 h-4 text-white" />
                                  ) : hasPending ? (
                                    <ClockIcon className="w-4 h-4 text-white" />
                                  ) : (
                                    <CheckCircleIcon className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                                  <div>
                                    <h4 className="text-lg font-semibold text-white truncate">
                                      {dog.hundename || 'Kein Name'}
                                    </h4>
                                    <div className="flex items-center gap-2 text-sm text-white/80">
                                      <span>{dog.rasse || 'Unbekannt'}</span>
                                      <span className="mx-1">•</span>
                                      <span>{dog.geschlecht || 'Unbekannt'}</span>
                                      {dog.kastriert && (
                                        <>
                                          <span className="mx-1">•</span>
                                          <span>Kastriert</span>
                                        </>
                                      )}
                                    </div>
                                    <span className="text-sm text-white/80 flex items-center gap-1 mt-1">
                                      <CalendarDaysIcon className="w-3 h-3" />
                                      {dog.geburtstag ? 
                                        `${Math.floor((new Date().getTime() - new Date(dog.geburtstag).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} Jahre` 
                                        : 'Unbekannt'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleEditDog(dog, member.member_nr)}
                                      className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 transform hover:scale-110 shadow-lg"
                                      title="Bearbeiten"
                                    >
                                      <PencilSquareIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDog(dog.dog_id)}
                                      className="p-2 bg-error-500 hover:bg-error-600 text-white rounded-lg transition-all duration-200 transform hover:scale-110 shadow-lg"
                                      title="Löschen"
                                    >
                                      <TrashIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Mobile Status Cards */}
                              <div className="px-2 py-3 space-y-3">
                                {/* Tollwut Status Card - Mobile */}
                                <div
                                  className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border cursor-pointer transition-all hover:shadow-lg overflow-hidden ${
                                    dog.valid_tollwut ? 'border-success-100 dark:border-success-700' : 'border-warning-100 dark:border-warning-700'
                                  }`}
                                  onClick={() => handleViewDocuments(dog, 'tollwut')}
                                >
                                  <div className="p-3">
                                    {/* Status Badge */}
                                    <div className={`inline-block mb-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                      dog.valid_tollwut ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                                    }`}>
                                      {dog.valid_tollwut ? 'AKTIV' : 'AUSSTEHEND'}
                                    </div>
                                    {/* Header */}
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className={`p-2.5 rounded-xl ${
                                        dog.valid_tollwut ? 'bg-success-50' : 'bg-warning-50'
                                      }`}>
                                        <BeakerIcon className={`w-5 h-5 ${
                                          dog.valid_tollwut ? 'text-success-600' : 'text-warning-600'
                                        }`} />
                                      </div>
                                      <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Tollwutimpfung</h4>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {dog.documents?.tollwut?.length || 0} {(dog.documents?.tollwut?.length || 0) === 1 ? 'Dokument' : 'Dokumente'}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Status Info */}
                                    <div className="space-y-2">
                                      {dog.tollwut_end_date && (
                                        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                          <span className="text-xs text-gray-600 dark:text-gray-400">Gültig bis</span>
                                          <span className={`text-xs font-medium ${
                                            new Date(dog.tollwut_end_date) > new Date() ? 'text-gray-900 dark:text-white' : 'text-warning-600'
                                          }`}>
                                            {formatDate(dog.tollwut_end_date)}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Action Button */}
                                      {(dog.valid_tollwut || (dog.documents?.tollwut?.length || 0) > 0) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (dog.valid_tollwut) {
                                              handleRevokeTollwut(dog.dog_id);
                                            } else {
                                              handleValidateTollwut(dog.dog_id);
                                            }
                                          }}
                                          className={`w-full py-2 rounded-lg text-xs font-medium transition-all ${
                                            dog.valid_tollwut
                                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                              : 'bg-primary-600 text-white hover:bg-primary-700'
                                          }`}
                                        >
                                          {dog.valid_tollwut ? 'Status entziehen' : 'Jetzt bestätigen'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Insurance Status Card - Mobile */}
                                <div
                                  className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border cursor-pointer transition-all hover:shadow-lg overflow-hidden ${
                                    dog.valid_insurance ? 'border-success-100 dark:border-success-700' : 'border-warning-100 dark:border-warning-700'
                                  }`}
                                  onClick={() => handleViewDocuments(dog, 'insurance')}
                                >
                                  <div className="p-3">
                                    {/* Status Badge */}
                                    <div className={`inline-block mb-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                      dog.valid_insurance ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                                    }`}>
                                      {dog.valid_insurance ? 'AKTIV' : 'AUSSTEHEND'}
                                    </div>
                                    {/* Header */}
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className={`p-2.5 rounded-xl ${
                                        dog.valid_insurance ? 'bg-success-50' : 'bg-warning-50'
                                      }`}>
                                        <ShieldCheckIcon className={`w-5 h-5 ${
                                          dog.valid_insurance ? 'text-success-600' : 'text-warning-600'
                                        }`} />
                                      </div>
                                      <div className="flex-1">
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Haftpflichtversicherung</h4>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {dog.documents?.insurance?.length || 0} {(dog.documents?.insurance?.length || 0) === 1 ? 'Dokument' : 'Dokumente'}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Status Info */}
                                    <div className="space-y-2">
                                      {dog.insurance_name && (
                                        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                                            {dog.insurance_name}
                                          </span>
                                        </div>
                                      )}
                                      
                                      {/* Action Button */}
                                      {(dog.valid_insurance || (dog.documents?.insurance?.length || 0) > 0) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (dog.valid_insurance) {
                                              handleRevokeInsurance(dog.dog_id);
                                            } else {
                                              handleValidateInsurance(dog.dog_id);
                                            }
                                          }}
                                          className={`w-full py-2 rounded-lg text-xs font-medium transition-all ${
                                            dog.valid_insurance
                                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                              : 'bg-primary-600 text-white hover:bg-primary-700'
                                          }`}
                                        >
                                          {dog.valid_insurance ? 'Status entziehen' : 'Jetzt bestätigen'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Desktop View */}
                            <div className="hidden sm:block p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-start gap-5">
                                    {/* Dog Profile Image - Larger Display */}
                                    <div className="h-24 w-24 rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-800 shadow-lg flex-shrink-0">
                                      <img
                                        src={`${BASE_URL}/api/dogs/image-url/${dog.dog_id}?size=M&token=${localStorage.getItem('jwt_token')}&t=${dog.image_path_updated || ''}`}
                                        alt={dog.hundename}
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          const parent = e.currentTarget.parentElement;
                                          if (parent) {
                                            parent.innerHTML = `
                                              <div class="h-full w-full flex items-center justify-center bg-primary-600">
                                                <svg class="h-12 w-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                  <path d="M4.5 10.5C4.5 7.5 7 5 10 5s5.5 2.5 5.5 5.5c0 2.28-1.2 4.3-3 5.5v4.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5V16c-1.8-1.2-3-3.22-3-5.5zm2 0c0 1.48.8 2.77 2 3.48v3.52c0 .28.22.5.5.5s.5-.22.5-.5v-3.52c1.2-.71 2-2 2-3.48 0-2.21-1.79-4-4-4s-4 1.79-4 4zm11.5 1c0 1.5-.5 2.9-1.4 4l.9.5c1-1.3 1.5-2.8 1.5-4.5 0-3.9-3.1-7-7-7-.7 0-1.4.1-2 .3l.5.9c.5-.1 1-.2 1.5-.2 3.3 0 6 2.7 6 6z"/>
                                                </svg>
                                              </div>
                                            `;
                                          }
                                        }}
                                      />
                                    </div>
                                  
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <h5 className="text-base font-semibold text-gray-900 dark:text-white">
                                        {dog.hundename}
                                      </h5>
                                      {getDogStatusIcon(dog)}
                                      <span className="text-sm px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-400">
                                        {dog.geschlecht || 'Unbekannt'}
                                      </span>
                                      {dog.kastriert && (
                                        <span className="text-sm px-2 py-1 bg-info-100 dark:bg-info-900/20 rounded-full text-info-700 dark:text-info-400">
                                          Kastriert
                                        </span>
                                      )}
                                    </div>

                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                      <div className="flex items-start gap-2">
                                        <BeakerIcon className="h-5 w-5 text-primary-500 mt-0.5" />
                                        <div>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Rasse</p>
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {dog.rasse || 'Unbekannt'}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-start gap-2">
                                        <CalendarDaysIcon className="h-5 w-5 text-primary-500 mt-0.5" />
                                        <div>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Alter</p>
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {dog.geburtstag ? 
                                              `${Math.floor((new Date().getTime() - new Date(dog.geburtstag).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} Jahre` 
                                              : 'Unbekannt'}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-start gap-2">
                                        <DocumentTextIcon className="h-5 w-5 text-primary-500 mt-0.5" />
                                        <div>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">Hundebox</p>
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {dog.hundebox === 'ja' ? 'Vorhanden' : dog.hundebox === 'nein' ? 'Nicht vorhanden' : 'Keine Info'}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Insurance and Vaccination Cards */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
                                      {/* Insurance Card with Corner Label */}
                                      <div className={`relative rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                                        dog.valid_insurance ? 'border-success-200 bg-success-50/30' : 'border-warning-200 bg-warning-50/30'
                                      }`}>
                                        {/* Corner Label */}
                                        <div className={`absolute top-0 left-0 px-2.5 py-1 rounded-br-lg flex items-center gap-1 ${
                                          dog.valid_insurance ? 'bg-success-500' : 'bg-warning-500'
                                        }`}>
                                          <ShieldCheckIcon className="w-3 h-3 text-white" />
                                          <span className="text-[10px] font-bold text-white uppercase tracking-wide">Haftpflicht</span>
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="pt-10 pb-3 px-3">
                                        
                                        {/* Document Preview */}
                                        {dog.documents?.insurance?.length > 0 ? (
                                          <div className="mb-2">
                                            <button
                                              onClick={() => handleViewDocuments(dog, 'insurance')}
                                              className="w-full group"
                                            >
                                              <div className="bg-white dark:bg-gray-700 rounded p-2 border border-gray-200 dark:border-gray-600 group-hover:border-primary-500 transition-colors">
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-2">
                                                    <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                      {dog.documents.insurance.length} Dokument{dog.documents.insurance.length !== 1 ? 'e' : ''}
                                                    </span>
                                                  </div>
                                                  <EyeIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-500" />
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="mb-2 text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                                            <DocumentTextIcon className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              Kein Dokument hochgeladen
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Action Buttons */}
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => {
                                              setSelectedDog(dog);
                                              setSelectedDocType('insurance');
                                              setShowAddDocumentModal(true);
                                            }}
                                            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-1.5"
                                          >
                                            <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                                            Upload
                                          </button>
                                          {dog.documents?.insurance?.length > 0 && !dog.valid_insurance && (
                                            <button
                                              onClick={() => handleValidateInsurance(dog.dog_id)}
                                              className="flex-1 px-3 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                              <CheckIcon className="h-4 w-4 mr-1" />
                                              Bestätigen
                                            </button>
                                          )}
                                          {dog.valid_insurance && (
                                            <button
                                              onClick={() => handleRevokeInsurance(dog.dog_id)}
                                              className="flex-1 px-3 py-2 bg-success-100 text-success-700 text-sm font-semibold rounded-lg hover:bg-success-200 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                              <XMarkIcon className="h-4 w-4 mr-1" />
                                              Entziehen
                                            </button>
                                          )}
                                        </div>
                                        
                                          {/* Validation Info */}
                                          {dog.valid_insurance && dog.insurance_checked_at && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                              Bestätigt am {new Date(dog.insurance_checked_at).toLocaleDateString('de-DE')}
                                              {dog.insurance_checked_by && ` von ${dog.insurance_checked_by}`}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Tollwut Card with Corner Label */}
                                      <div className={`relative rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                                        dog.valid_tollwut ? 'border-success-200 bg-success-50/30' : 'border-warning-200 bg-warning-50/30'
                                      }`}>
                                        {/* Corner Label */}
                                        <div className={`absolute top-0 left-0 px-2.5 py-1 rounded-br-lg flex items-center gap-1 ${
                                          dog.valid_tollwut ? 'bg-success-500' : 'bg-warning-500'
                                        }`}>
                                          <BeakerIcon className="w-3 h-3 text-white" />
                                          <span className="text-[10px] font-bold text-white uppercase tracking-wide">Tollwut</span>
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="pt-10 pb-3 px-3">
                                        
                                        {/* Document Preview */}
                                        {dog.documents?.tollwut?.length > 0 ? (
                                          <div className="mb-2">
                                            <button
                                              onClick={() => handleViewDocuments(dog, 'tollwut')}
                                              className="w-full group"
                                            >
                                              <div className="bg-white dark:bg-gray-700 rounded p-2 border border-gray-200 dark:border-gray-600 group-hover:border-primary-500 transition-colors">
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-2">
                                                    <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                      {dog.documents.tollwut.length} Dokument{dog.documents.tollwut.length !== 1 ? 'e' : ''}
                                                    </span>
                                                  </div>
                                                  <EyeIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-500" />
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="mb-2 text-center p-3 bg-gray-100 dark:bg-gray-700 rounded">
                                            <DocumentTextIcon className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              Kein Dokument hochgeladen
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Expiry Date */}
                                        {dog.tollwut_end_date && (
                                          <div className="mb-2 p-2 bg-white dark:bg-gray-700 rounded">
                                            <div className="flex items-center justify-between text-sm">
                                              <span className="text-gray-600 dark:text-gray-400">Gültig bis:</span>
                                              <span className={`font-medium ${
                                                new Date(dog.tollwut_end_date) < new Date() 
                                                  ? 'text-error-600' 
                                                  : 'text-gray-900 dark:text-white'
                                              }`}>
                                                {new Date(dog.tollwut_end_date).toLocaleDateString('de-DE')}
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Action Buttons */}
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => {
                                              setSelectedDog(dog);
                                              setSelectedDocType('tollwut');
                                              setShowAddDocumentModal(true);
                                            }}
                                            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-1.5"
                                          >
                                            <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                                            Upload
                                          </button>
                                          {dog.documents?.tollwut?.length > 0 && !dog.valid_tollwut && (
                                            <button
                                              onClick={() => handleValidateTollwut(dog.dog_id)}
                                              className="flex-1 px-3 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                              <CheckIcon className="h-4 w-4 mr-1" />
                                              Bestätigen
                                            </button>
                                          )}
                                          {dog.valid_tollwut && (
                                            <button
                                              onClick={() => handleRevokeTollwut(dog.dog_id)}
                                              className="flex-1 px-3 py-2 bg-success-100 text-success-700 text-sm font-semibold rounded-lg hover:bg-success-200 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                              <XMarkIcon className="h-4 w-4 mr-1" />
                                              Entziehen
                                            </button>
                                          )}
                                        </div>
                                        
                                          {/* Validation Info */}
                                          {dog.valid_tollwut && dog.tollwut_checked_at && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                              Bestätigt am {new Date(dog.tollwut_checked_at).toLocaleDateString('de-DE')}
                                              {dog.tollwut_checked_by && ` von ${dog.tollwut_checked_by}`}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-1 ml-4">
                                <button
                                  onClick={() => handleEditDog(dog, member.member_nr)}
                                  className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200 transform hover:scale-110"
                                  title="Bearbeiten"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDog(dog.dog_id)}
                                  className="p-2 text-error-600 hover:text-error-700 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-all duration-200 transform hover:scale-110"
                                  title="Löschen"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Dog Modal */}
      <AddDogModal
        isOpen={showAddDogModal}
        onClose={() => setShowAddDogModal(false)}
        memberId={selectedMemberId || ''}
        onSubmit={handleAddDogSubmit}
      />

      {/* Edit Dog Modal */}
      {selectedDog && (
        <EditDogModal
          isOpen={showEditDogModal}
          onClose={() => setShowEditDogModal(false)}
          dog={selectedDog}
          onSubmit={handleEditDogSubmit}
        />
      )}

      {/* Add Document Modal - Mobile Optimized */}
      {showAddDocumentModal && selectedDog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-4 sm:p-6 animate-slide-up sm:animate-none">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Dokument hochladen - {selectedDocType === 'insurance' ? 'Versicherung' : 'Tollwutimpfung'}
              </h3>
              <button
                onClick={() => setShowAddDocumentModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              formData.append('doc_type', selectedDocType);
              
              try {
                const response = await apiClient.post(
                  `/api/dogs/upload_dog_document/${selectedDog.dog_id}`,
                  formData
                );
                if (response.data.success) {
                  await fetchAllDogsStatus();
                  setShowAddDocumentModal(false);
                  setConfirmModal({
                    visible: true,
                    type: 'success',
                    title: 'Erfolgreich',
                    message: 'Dokument erfolgreich hochgeladen!',
                    variant: 'single'
                  });
                }
              } catch (err: any) {
                setConfirmModal({
                  visible: true,
                  type: 'error',
                  title: 'Fehler',
                  message: 'Fehler beim Hochladen des Dokuments.',
                  variant: 'single'
                });
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dokument auswählen
                </label>
                <input
                  type="file"
                  name="document"
                  required
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Erlaubte Formate: PDF, JPG, PNG (max. 10MB)
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddDocumentModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                >
                  <CloudArrowUpIcon className="h-5 w-5" />
                  Hochladen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Viewer Modal - Mobile Optimized */}
      {showDocumentModal && selectedDog && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-5xl h-[85vh] sm:h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 animate-slide-up sm:animate-none">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {documentType === 'insurance' ? 'Versicherungsdokumente' : 'Tollwutimpfung'} - {selectedDog.hundename}
                </h3>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all hover:scale-110"
                title="Schließen"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {selectedDocuments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                    Keine Dokumente vorhanden
                  </p>
                  <label className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors cursor-pointer flex items-center gap-2 shadow-lg hover:shadow-xl">
                    <CloudArrowUpIcon className="h-5 w-5" />
                    Dokument hochladen
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file && selectedDog) {
                          const formData = new FormData();
                          formData.append('document', file);
                          formData.append('doc_type', documentType);
                          
                          try {
                            const response = await apiClient.post(
                              `/api/dogs/upload_dog_document/${selectedDog.dog_id}`,
                              formData
                            );
                            if (response.data.success) {
                              await fetchAllDogsStatus();
                              setShowDocumentModal(false);
                              setConfirmModal({
                                visible: true,
                                type: 'success',
                                title: 'Erfolgreich',
                                message: 'Dokument erfolgreich hochgeladen!',
                                variant: 'single'
                              });
                            }
                          } catch (err: any) {
                            setConfirmModal({
                              visible: true,
                              type: 'error',
                              title: 'Fehler',
                              message: 'Fehler beim Hochladen des Dokuments.',
                              variant: 'single'
                            });
                          }
                        }
                      }}
                    />
                  </label>
                </div>
              ) : (
                <div className="h-full flex flex-col">
                  {/* Navigation Bar */}
                  <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setCurrentDocIndex(Math.max(0, currentDocIndex - 1))}
                      disabled={currentDocIndex === 0}
                      className="p-3 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-110"
                      title="Vorheriges Dokument"
                    >
                      <ChevronLeftIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                    </button>
                    
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Dokument {currentDocIndex + 1} von {selectedDocuments.length}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {selectedDocuments[currentDocIndex].filename}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => setCurrentDocIndex(Math.min(selectedDocuments.length - 1, currentDocIndex + 1))}
                      disabled={currentDocIndex === selectedDocuments.length - 1}
                      className="p-3 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-110"
                      title="Nächstes Dokument"
                    >
                      <ChevronRightIcon2 className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                    </button>
                  </div>

                  {/* Document Viewer */}
                  <div className="flex-1 p-6 bg-gray-100 dark:bg-gray-900 overflow-auto">
                    {selectedDocuments[currentDocIndex].filename.toLowerCase().endsWith('.pdf') ? (
                      <iframe
                        src={`${BASE_URL}/api/members/documents/download-url/${selectedDocuments[currentDocIndex].docId}?token=${localStorage.getItem('jwt_token')}`}
                        className="w-full h-full rounded-lg bg-white shadow-lg"
                        title="PDF Viewer"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <img
                          src={`${BASE_URL}/api/members/documents/download-url/${selectedDocuments[currentDocIndex].docId}?token=${localStorage.getItem('jwt_token')}`}
                          alt={selectedDocuments[currentDocIndex].filename}
                          className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                        />
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Hochgeladen am {new Date(selectedDocuments[currentDocIndex].created_at).toLocaleDateString('de-DE')}
                      </div>
                      
                      <div className="flex gap-3">
                        {/* Validate Button */}
                        {((documentType === 'insurance' && !selectedDog.valid_insurance) ||
                          (documentType === 'tollwut' && !selectedDog.valid_tollwut)) && (
                          <button
                            onClick={async () => {
                              if (documentType === 'insurance') {
                                await handleValidateInsurance(selectedDog.dog_id);
                              } else {
                                await handleValidateTollwut(selectedDog.dog_id);
                              }
                              setShowDocumentModal(false);
                            }}
                            className="px-4 py-2 bg-success-500 text-white rounded-lg hover:bg-success-600 transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                            {documentType === 'insurance' ? 'Versicherung' : 'Impfung'} bestätigen
                          </button>
                        )}
                        
                        {/* Download Button */}
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${BASE_URL}/api/members/documents/download-url/${selectedDocuments[currentDocIndex].docId}?token=${localStorage.getItem('jwt_token')}`;
                            link.download = selectedDocuments[currentDocIndex].filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                          title="Dokument herunterladen"
                        >
                          <DocumentArrowDownIcon className="h-5 w-5" />
                          Download
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => {
                            setConfirmModal({
                              visible: true,
                              type: 'warning',
                              title: 'Dokument löschen',
                              message: 'Möchten Sie dieses Dokument wirklich löschen?',
                              variant: 'two',
                              onConfirm: async () => {
                                try {
                                  const response = await apiClient.delete(
                                    `/api/dogs/delete_dog_document/${selectedDocuments[currentDocIndex].docId}`
                                  );
                                  if (response.data.success) {
                                    setConfirmModal({
                                      visible: true,
                                      type: 'success',
                                      title: 'Erfolgreich',
                                      message: 'Dokument erfolgreich gelöscht!',
                                      variant: 'single'
                                    });
                                    
                                    // Refresh documents
                                    await fetchAllDogsStatus();
                                    
                                    // Update local state
                                    const newDocs = selectedDocuments.filter((_, idx) => idx !== currentDocIndex);
                                    setSelectedDocuments(newDocs);
                                    
                                    if (newDocs.length === 0) {
                                      setShowDocumentModal(false);
                                    } else {
                                      // Adjust index if needed
                                      if (currentDocIndex >= newDocs.length) {
                                        setCurrentDocIndex(newDocs.length - 1);
                                      }
                                    }
                                  }
                                } catch (err: any) {
                                  setConfirmModal({
                                    visible: true,
                                    type: 'error',
                                    title: 'Fehler',
                                    message: 'Fehler beim Löschen des Dokuments.',
                                    variant: 'single'
                                  });
                                }
                              }
                            });
                          }}
                          className="px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                          title="Dokument löschen"
                        >
                          <TrashIcon className="h-5 w-5" />
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ConfirmationModal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal(prev => ({ ...prev, visible: false }))
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal(prev => ({ ...prev, visible: false }))
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              if (confirmModal.onConfirm) confirmModal.onConfirm();
              setConfirmModal(prev => ({ ...prev, visible: false }));
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default DogManagement;